import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import {
  ArrowRight,
  BadgePercent,
  BookOpen,
  CreditCard,
  Download,
  LoaderCircle,
  Mail,
  ShieldCheck,
  ShoppingBag,
  Trash2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { SiteHeader } from "@/features/sales/storefront/components/SiteHeader";
import { SiteFooter } from "@/features/sales/storefront/components/SiteFooter";
import { PageIntro } from "@/features/sales/storefront/components/PageIntro";
import { useCart } from "@/features/sales/storefront/cart/CartContext";
import { useCatalog } from "@/features/sales/storefront/catalog/CatalogContext";
import { getProductCategories } from "@/features/sales/storefront/catalog/data";
import { useCurrency } from "@/features/sales/storefront/currency/CurrencyContext";
import { useAuth } from "@/features/auth/AuthContext";
import { trackEvent } from "@/lib/analytics";
import { appCheckFetch } from "@/lib/firebase";
import { LEGAL_DOCUMENT_HASHES, LEGAL_DOCUMENT_VERSIONS } from "@/features/legal/legal-config";

interface EPaycoCheckoutInstance {
  open: () => void;
}

declare global {
  interface Window {
    ePayco?: {
      checkout: {
        configure: (options: {
          sessionId: string;
          type: "standard" | "onpage";
          test: boolean;
        }) => EPaycoCheckoutInstance;
      };
    };
  }
}

let epaycoScriptPromise: Promise<void> | undefined;

function loadEPaycoCheckout() {
  if (window.ePayco?.checkout) return Promise.resolve();
  if (epaycoScriptPromise) return epaycoScriptPromise;

  epaycoScriptPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      'script[src="https://checkout.epayco.co/checkout-v2.js"]',
    );
    const script = existing ?? document.createElement("script");
    const onLoad = () =>
      window.ePayco?.checkout
        ? resolve()
        : reject(new Error("ePayco no quedó disponible en el navegador."));
    script.addEventListener("load", onLoad, { once: true });
    script.addEventListener(
      "error",
      () => reject(new Error("No pudimos cargar el checkout seguro de ePayco.")),
      { once: true },
    );
    if (!existing) {
      script.src = "https://checkout.epayco.co/checkout-v2.js";
      script.async = true;
      document.head.appendChild(script);
    }
  });

  return epaycoScriptPromise;
}

export const Route = createFileRoute("/productos/carrito")({
  head: () => ({
    meta: [
      { title: "Tu carrito — Socrattica" },
      { name: "description", content: "Revisa y confirma tu compra en Socrattica." },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: CartPage,
});

function CartPage() {
  const { items, remove, clear, subtotal } = useCart();
  const { getProduct } = useCatalog();
  const { currency, formatPrice } = useCurrency();
  const { user } = useAuth();
  const userEmail = user?.email;
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [immediateDelivery, setImmediateDelivery] = useState(false);
  const [couponInput, setCouponInput] = useState("");
  const [couponCode, setCouponCode] = useState("");
  const [couponPercent, setCouponPercent] = useState(0);
  const [couponLoading, setCouponLoading] = useState(false);
  const discount = couponCode ? Math.round(subtotal * couponPercent) / 100 : 0;
  const total = Math.round((subtotal - discount) * 100) / 100;

  useEffect(() => {
    if (userEmail) setEmail((current) => current || userEmail);
  }, [userEmail]);

  const handleNewsletterDiscount = async () => {
    if (!email.match(/.+@.+\..+/)) {
      toast.error("Ingresa primero el correo asociado al cupón");
      return;
    }
    const normalizedCoupon = couponInput.trim().toUpperCase();
    if (!normalizedCoupon) {
      toast.error("Escribe el código del cupón");
      return;
    }
    setCouponLoading(true);
    try {
      const response = await appCheckFetch("/api/newsletter-discount", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), code: normalizedCoupon }),
      });
      const data = (await response.json().catch(() => null)) as {
        code?: string;
        percent?: number;
        message?: string;
      } | null;
      if (!response.ok || !data?.code) {
        throw new Error(
          data?.message ||
            "No recibimos una respuesta válida al comprobar el cupón. Recarga la página e inténtalo nuevamente.",
        );
      }
      setCouponCode(data.code);
      setCouponPercent(data.percent ?? 0);
      toast.success(`¡Tu descuento del ${data.percent ?? 0}% está aplicado!`);
    } catch (error) {
      setCouponCode("");
      setCouponPercent(0);
      toast.error(error instanceof Error ? error.message : "No pudimos verificar tu suscripción.");
    } finally {
      setCouponLoading(false);
    }
  };

  const products = items
    .map((i) => ({ item: i, product: getProduct(i.productId) }))
    .filter((x) => x.product);

  useEffect(() => {
    const analyticsItems = items.flatMap((item) => {
      const product = getProduct(item.productId);
      if (!product) return [];
      return [
        {
          item_id: product.id,
          item_name: product.title,
          price: product.price,
          quantity: item.quantity,
        },
      ];
    });
    if (analyticsItems.length === 0) return;
    void trackEvent("view_cart", {
      currency,
      value: subtotal,
      items_count: analyticsItems.length,
      items: analyticsItems,
    });
  }, [currency, getProduct, items, subtotal]);

  const handleCheckout = async () => {
    if (!email.match(/.+@.+\..+/)) {
      toast.error("Ingresa un correo válido");
      return;
    }
    if (!acceptedTerms) {
      toast.error("Acepta los términos de uso y de compra para continuar");
      return;
    }
    if (!immediateDelivery) {
      toast.error("Confirma la entrega digital inmediata para continuar");
      return;
    }
    if (products.length === 0) return;
    void trackEvent("begin_checkout", {
      currency,
      value: subtotal,
      items_count: products.length,
      items: products.map(({ item, product }) => ({
        item_id: product!.id,
        item_name: product!.title,
        price: product!.price,
        quantity: item.quantity,
      })),
    });
    setSubmitting(true);
    try {
      const idToken = user ? await user.getIdToken() : undefined;
      const response = await appCheckFetch("/api/epayco-checkout", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
        },
        body: JSON.stringify({
          email: email.trim(),
          items: items.map((item) => ({
            productId: item.productId,
            quantity: item.quantity,
          })),
          acceptedTerms,
          immediateDelivery,
          couponCode: couponCode || undefined,
          legalVersions: LEGAL_DOCUMENT_VERSIONS,
          legalHashes: LEGAL_DOCUMENT_HASHES,
        }),
      });
      const data = (await response.json().catch(() => null)) as {
        sessionId?: string;
        orderId?: string;
        testMode?: boolean;
        total?: number;
        discount?: number;
        message?: string;
      } | null;
      if (!response.ok || !data?.sessionId) {
        throw new Error(data?.message || "No pudimos iniciar el pago.");
      }
      try {
        window.sessionStorage.setItem(
          "socrattica:active-checkout",
          JSON.stringify({
            orderId: data.orderId,
            currency,
            value: data.total ?? total,
            items: products.map(({ item, product }) => ({
              item_id: product!.id,
              item_name: product!.title,
              price: product!.price,
              quantity: item.quantity,
            })),
          }),
        );
      } catch {
        // El checkout debe continuar aunque el navegador bloquee sessionStorage.
      }

      await loadEPaycoCheckout();
      const checkout = window.ePayco?.checkout.configure({
        sessionId: data.sessionId,
        type: "standard",
        test: data.testMode === true,
      });
      if (!checkout) throw new Error("No pudimos abrir ePayco.");
      checkout.open();
      window.setTimeout(() => setSubmitting(false), 2_000);
    } catch (error) {
      setSubmitting(false);
      toast.error(error instanceof Error ? error.message : "No pudimos abrir ePayco.");
    }
  };

  return (
    <div className="flex min-h-screen flex-col overflow-x-hidden bg-white text-black">
      <SiteHeader />
      <main id="main-content" className="flex-1">
        <PageIntro
          compact
          eyebrow="Tu selección"
          title="Tu carrito."
          description="Revisa tus recursos y completa la compra para recibir el acceso por correo."
          aside={
            <div>
              <p className="text-5xl font-semibold tracking-[-0.06em]">{products.length}</p>
              <p className="mt-2 text-xs text-white/50">
                producto{products.length !== 1 ? "s" : ""} en tu carrito
              </p>
            </div>
          }
          details={["Revisa", "Confirma", "Descarga"]}
        />

        <section className="py-12 sm:py-16">
          <div className="mx-auto max-w-7xl px-4 sm:px-6">
            {products.length === 0 ? (
              <div className="rounded-xl border border-black/15 bg-white px-6 py-16 text-center sm:py-20">
                <div className="mx-auto flex max-w-xl flex-col items-center">
                  <span className="flex h-12 w-12 items-center justify-center rounded-full bg-black text-white">
                    <ShoppingBag className="h-5 w-5" />
                  </span>
                  <h2 className="mt-5 text-2xl font-semibold tracking-[-0.04em]">
                    Tu carrito está vacío.
                  </h2>
                  <p className="mt-3 max-w-md text-xs leading-5 text-black/50">
                    Explora la tienda y agrega el recurso que necesitas.
                  </p>
                  <Button asChild className="mt-6">
                    <Link to="/productos/catalogo">
                      Explorar librería
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  </Button>
                </div>
              </div>
            ) : (
              <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_400px]">
                <div className="min-w-0">
                  <div className="mb-5 flex items-center justify-between gap-4">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.14em] text-black/60">
                        Tus recursos
                      </p>
                      <h2 className="mt-1 text-xl font-semibold tracking-[-0.03em]">
                        Libros seleccionados
                      </h2>
                    </div>
                    <span className="rounded-full border border-black/10 bg-white px-4 py-2 text-sm text-black/50">
                      {products.length} producto{products.length !== 1 ? "s" : ""}
                    </span>
                  </div>

                  <div className="space-y-4">
                    {products.map(({ item, product }) => (
                      <div
                        key={item.productId}
                        className="flex flex-wrap gap-4 rounded-xl border border-black/15 bg-white p-4 transition-colors hover:border-black sm:flex-nowrap sm:p-5"
                      >
                        <div className="flex h-28 w-24 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-black p-1 text-white">
                          {product!.coverThumbnail || product!.coverImage ? (
                            <img
                              src={product!.coverThumbnail ?? product!.coverImage}
                              alt={`Portada de ${product!.title}`}
                              className="h-full w-full object-contain"
                            />
                          ) : (
                            <BookOpen className="h-5 w-5" aria-hidden="true" />
                          )}
                        </div>
                        <div className="min-w-0 flex-1 py-1">
                          <div className="text-xs font-medium uppercase tracking-wide text-black/40">
                            {getProductCategories(product!)
                              .map((category) => category.replace(/-/g, " "))
                              .join(" · ")}
                            {product!.level ? ` · ${product!.level}` : ""}
                          </div>
                          <h3 className="mt-1 truncate text-base font-semibold">
                            {product!.title}
                          </h3>
                          <div className="mt-1 text-sm text-black/50">
                            {formatPrice(product!.price, product!.currency)}
                          </div>
                          <div className="mt-4 flex flex-wrap items-center gap-2">
                            <span className="inline-flex h-9 items-center rounded-lg border border-black/15 bg-white px-3 text-xs text-black/55">
                              1 licencia personal
                            </span>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="rounded-lg text-black/45 hover:border-black hover:text-black"
                              onClick={() => remove(item.productId)}
                            >
                              <Trash2 className="h-4 w-4" /> Eliminar
                            </Button>
                          </div>
                        </div>
                        <div className="ml-auto text-right text-lg font-semibold text-black">
                          {formatPrice(product!.price * item.quantity, product!.currency)}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="mt-5 flex justify-end">
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="rounded-lg text-black/45 hover:border-black hover:text-black"
                        >
                          <Trash2 className="h-4 w-4" /> Vaciar carrito
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="rounded-xl border-black bg-white p-7 text-black">
                        <AlertDialogHeader>
                          <AlertDialogTitle className="text-xl font-semibold">
                            ¿Vaciar el carrito?
                          </AlertDialogTitle>
                          <AlertDialogDescription className="text-black/55">
                            Se eliminarán todos los productos del carrito.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel className="rounded-lg border-black bg-white text-black hover:bg-black hover:text-white">
                            Cancelar
                          </AlertDialogCancel>
                          <AlertDialogAction
                            className="rounded-lg bg-black text-white hover:bg-white hover:text-black"
                            onClick={clear}
                          >
                            Vaciar
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>

                <aside className="sticky top-24 h-fit rounded-xl border border-black/15 bg-white p-5 sm:p-6">
                  <div className="flex items-center gap-3 border-b border-black/5 pb-4">
                    <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-black text-white">
                      <CreditCard className="h-4 w-4" />
                    </span>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.14em] text-black/60">
                        Pago seguro
                      </p>
                      <h2 className="text-lg font-semibold">Resumen</h2>
                    </div>
                  </div>

                  <div className="mt-4 space-y-1.5 text-sm">
                    <div className="flex justify-between text-black/55">
                      <span>Subtotal</span>
                      <span className="text-black">{formatPrice(subtotal)}</span>
                    </div>
                    <div className="flex justify-between text-black/55">
                      <span>Formato</span>
                      <span className="text-black">Digital</span>
                    </div>
                    {couponCode && (
                      <div className="flex justify-between font-medium text-emerald-700">
                        <span>Descuento ({couponPercent}%)</span>
                        <span>-{formatPrice(discount)}</span>
                      </div>
                    )}
                  </div>
                  <div className="my-4 border-t border-black/5" />
                  <div className="flex justify-between text-lg font-semibold">
                    <span>Total</span>
                    <span>{formatPrice(total)}</span>
                  </div>
                  {currency === "USD" && (
                    <p className="mt-2 text-xs leading-5 text-black/60">
                      Conversión estimada. El pago se procesa en pesos colombianos (COP).
                    </p>
                  )}

                  <div className="mt-5 space-y-4">
                    <div>
                      <Label
                        htmlFor="email"
                        className="text-xs font-medium uppercase tracking-widest text-black/45"
                      >
                        Correo para recibir tu acceso
                      </Label>
                      <div className="relative mt-2">
                        <Mail className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-black/35" />
                        <Input
                          id="email"
                          type="email"
                          value={email}
                          onChange={(e) => {
                            setEmail(e.target.value);
                            setCouponCode("");
                            setCouponPercent(0);
                          }}
                          className="pl-11"
                          placeholder="tu@correo.com"
                          required
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="coupon-code" className="text-xs font-medium uppercase tracking-widest text-black/45">
                        Código de descuento
                      </Label>
                          <div className="mt-2 grid grid-cols-[minmax(0,1fr)_auto] gap-2">
                            <Input
                              id="coupon-code"
                              value={couponInput}
                              onChange={(event) => {
                                setCouponInput(event.target.value.toUpperCase());
                                setCouponCode("");
                                setCouponPercent(0);
                              }}
                              placeholder="Código"
                              autoComplete="off"
                              className="font-mono uppercase"
                              aria-label="Código del cupón"
                            />
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="h-10 shrink-0 px-3"
                              disabled={couponLoading || Boolean(couponCode)}
                              onClick={handleNewsletterDiscount}
                            >
                              {couponLoading ? (
                                <LoaderCircle className="h-4 w-4 animate-spin" />
                              ) : (
                                <BadgePercent className="h-4 w-4" />
                              )}
                              {couponCode ? "Aplicado" : "Aplicar cupón"}
                            </Button>
                          </div>
                          {couponCode && (
                            <p className="mt-2 text-xs font-semibold text-emerald-700">
                              {couponPercent}% aplicado correctamente
                            </p>
                          )}
                    </div>
                    <div className="flex items-center gap-3 border-y border-black/10 py-3">
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-black text-white">
                          <CreditCard className="h-4 w-4" />
                        </span>
                        <p className="text-xs leading-5 text-black/60">
                          Pago seguro con PSE, tarjeta de crédito o débito.
                        </p>
                    </div>
                    <div className="space-y-3 rounded-lg bg-[#f7f6f1] p-3.5">
                      <label className="flex cursor-pointer items-start gap-3 text-xs leading-5 text-black/70">
                        <input
                          type="checkbox"
                          checked={acceptedTerms}
                          onChange={(event) => setAcceptedTerms(event.target.checked)}
                          className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer accent-black"
                        />
                        <span>
                          He leído y acepto los{" "}
                          <Link
                            to="/legal/terminos"
                            className="font-semibold text-black underline underline-offset-2"
                          >
                            términos de uso y de compra
                          </Link>{" "}
                          y la{" "}
                          <Link
                            to="/legal/privacidad"
                            className="font-semibold text-black underline underline-offset-2"
                          >
                            política de privacidad
                          </Link>
                          .
                        </span>
                      </label>
                      <label className="flex cursor-pointer items-start gap-3 text-xs leading-5 text-black/70">
                        <input
                          type="checkbox"
                          checked={immediateDelivery}
                          onChange={(event) => setImmediateDelivery(event.target.checked)}
                          className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer accent-black"
                        />
                        <span>
                          Solicito que el acceso digital comience al aprobarse el pago. Entiendo que
                          una descarga iniciada puede afectar el retracto cuando la ley lo permita.
                        </span>
                      </label>
                    </div>
                    <Button
                      size="lg"
                      className="w-full"
                      disabled={submitting}
                      onClick={handleCheckout}
                    >
                      {submitting ? (
                        <>
                          <LoaderCircle className="h-4 w-4 animate-spin" />
                          Redirigiendo...
                        </>
                      ) : (
                        <>
                          Pagar {formatPrice(total)} <ArrowRight className="h-4 w-4" />
                        </>
                      )}
                    </Button>
                    <p className="flex items-start justify-center gap-2 text-center text-xs leading-relaxed text-black/60">
                      <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-black" />
                      Revisa la{" "}
                      <Link to="/legal/reembolsos" className="underline underline-offset-2">
                        política de reembolsos
                      </Link>{" "}
                      y las{" "}
                      <Link to="/legal/licencia" className="underline underline-offset-2">
                        condiciones de licencia
                      </Link>
                      .
                    </p>
                    <div className="flex items-center justify-center gap-2 text-xs text-black/50">
                      <Download className="h-3.5 w-3.5" />
                      Descarga privada disponible desde tu cuenta
                    </div>
                  </div>
                </aside>
              </div>
            )}
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
